import {
    Column,
    dummySelectWrapper,
    Group,
    GroupProps,
    Insert,
    lookupNestedProp,
    matchAnywhere,
    setNestedProp,
    TID
} from "./Table";
import {useContext, useEffect, useRef} from "react";
import {getEditable, getEditKey} from "./TableEditCell";
import {EditContext} from "./EditProvider";
import {Calculator} from "./Calculator";
import {autoFormat} from "../../pages/project/manage/summary/AutoFormat";

export function useTableWebMcp<T extends TID, U extends TID>(props: {
    name: string;
    data: Group<T>[];
    columns: Column<T>[]
    insert?: Insert;
    noFooter?: boolean;
    onRowSelect?(input: T): void
    noHorizontalScroller?: boolean;
    beforeGroup?: (props: GroupProps<T>) => any
    afterGroup?: (props: GroupProps<T>) => any
    hasGroupStart?: (group: Group<T>) => boolean;
    hasGroupEnd?: (group: Group<T>) => boolean;
    childTableData?: (input: T) => U[] | undefined;
    childTableColumns?: (input: T) => (Column<U>|null|false)[] | undefined;
    childTableRowStyle?: (input: U) => any | undefined;
    blankElement?: any;
}) {
    const propsRef = useRef(props);
    propsRef.current = props;

    // update comes from EditProvider (this hook runs inside TableInner, a descendant of it)
    // and drives the same edit path as EditCell. Keep it in a ref so we don't re-register.
    const {update} = useContext(EditContext);
    const updateRef = useRef(update);
    updateRef.current = update;

    useEffect(() => {
        if(!("modelContext" in document) || !document.modelContext) return;

        const controller = new AbortController();

        const mc = document.modelContext;
        const columns = () => propsRef.current.columns;
        const rows = () => rowsOf(propsRef.current.data);

        // registerTool returns void; the AbortSignal is the teardown handle.
        // Aborting the controller on cleanup unregisters every tool registered
        // with it, which also avoids colliding with a re-mounted table's tools.
        const register = (descriptor: ModelContextToolDescriptor) => {
            try {
                mc.registerTool(descriptor, { signal: controller.signal });
            } catch(e) {
                console.error(e);
            }
        };

        register({
            name: "schema_table_" + props.name,
            // describes the table: its columns (data-type, filterable, sortable, editable) and total row-count.
            description: `Describe the "${propsRef.current.name}" table: its columns (data-type, whether each is ` +
                `filterable, sortable and editable) and the total row-count. Call this before search/edit to learn the shape.`,
            inputSchema: {
                type: "object",
                properties: {},
            },
            execute: () => {
                return toolResult(tableSchema(propsRef.current.name, rows(), columns()));
            },
        });

        register({
            name: "search_table_" + props.name,
            // searches rendered table data using the column filters and returns the rendered table row elements along with an ID reference.
            description: `Search the "${propsRef.current.name}" table. Provide "query" to match text against every column ` +
                `(${columns().map(c => c.name).join(", ")}) and/or "columnFilters" to match specific columns individually. ` +
                `When both are given, a row must match "query" and every column filter. Within any single query, separate terms with ` +
                `"|" for an OR match (matches if any term matches), e.g. "steel|aluminum". Column filters combine with AND across columns. ` +
                `By default every column is returned; pass "columns" to return only those columns (see schema_table_${propsRef.current.name} for available names).`,
            inputSchema: {
                type: "object",
                properties: {
                    query: {type: "string", description: `Text to search for across all columns. Use "|" to OR multiple terms, e.g. "steel|aluminum".`},
                    columnFilters: {
                        type: "object",
                        description: `Per-column filters as a map of exact column name to query text. A row must match every provided ` +
                            `column (AND across columns); within each value use "|" to OR terms, e.g. {"Door Material": "steel", "Hand": "LH|RH"}.`,
                        additionalProperties: {type: "string"},
                    },
                    columns: {
                        type: "array",
                        description: `Exact column names to include in each result row. Omit to return all columns. ` +
                            `Filtering still considers every column regardless of this list.`,
                        items: {type: "string"},
                    },
                },
            },
            execute: (args: {query?: string, columnFilters?: {[column: string]: string}, columns?: string[]}) => {
                return toolResult(searchTable(rows(), columns(), args.query || "", args.columnFilters || {}, args.columns));
            },
        });

        register({
            name: "edit_cell_" + props.name,
            // allows the caller to edit a specific row by passing an ID reference, column name and new value.
            description: `Edit a cell in the "${propsRef.current.name}" table by row id, column name and new value. ` +
                `Editable columns: ${columns().filter(c => isEditable(c, propsRef.current.data)).map(c => c.name).join(", ") || "(none)"}.`,
            inputSchema: {
                type: "object",
                properties: {
                    rowId: {type: "number", description: "The id of the row to edit"},
                    column: {type: "string", description: "The name of the column to edit"},
                    value: {type: "string", description: "The new value (as text)"},
                },
                required: ["rowId", "column", "value"],
            },
            execute: async (args: {rowId: number, column: string, value: string}) => {
                const row = rows().find(r => r.id === args.rowId);
                if(!row) return toolResult({error: `no row with id ${args.rowId} in the "${propsRef.current.name}" table`});

                const column = columns().find(c => c.name === args.column);
                if(!column) return toolResult({error: `no column named "${args.column}" (available: ${columns().map(c => c.name).join(", ")})`});

                try {
                    const updated = applyEdit(row, column, args.value ?? "");
                    await updateRef.current(updated);
                    return toolResult({ok: true, rowId: args.rowId, column: args.column, value: args.value});
                } catch (e: any) {
                    return toolResult({error: e?.message || String(e)});
                }
            },
        });

        return () => {
            // aborting the signal unregisters every tool registered with it
            controller.abort();
        }
    }, [props.name, propsRef]);

    return null;
}

function rowsOf<T extends TID>(data: Group<T>[]): T[] {
    return data.reduce((acc, g) => acc.concat(g.rows), [] as T[]);
}

function isEditable<T extends TID>(col: Column<T>, data: Group<T>[]) {
    if(col.editable) return true;
    if(!col.editableFunc) return false;

    // editableFunc is row-dependent, so treat the column as editable if any row is editable
    return rowsOf(data).some(r => !!getEditable(col, r));
}

// cellText renders a column to a plain string, falling back to the raw value
// when the column renders a react element (e.g. checkbox/status columns)
function cellText<T extends TID>(row: T, col: Column<T>): string {
    try {
        const rendered = col.render(row, col);
        if(typeof rendered === "string" || typeof rendered === "number") {
            return String(rendered);
        }
    } catch (e) {
        // fall through to raw lookup
    }

    if(col.editKey) {
        const v = lookupNestedProp(row, col.editKey);
        if(v !== null && v !== undefined) return String(v);
    }

    return "";
}

// columnDataType maps a column to a coarse data-type the agent can reason about.
// editable columns carry an explicit type; view-only columns are reported as "text".
function columnDataType<T extends TID>(col: Column<T>, sampleRow: T | undefined): string {
    const editable = sampleRow ? getEditable(col, sampleRow) : col.editable;
    switch(editable?.type) {
        case "number": return "number";
        case "checkbox": return "boolean";
        case "date": return "date";
        case "select": return "select";
        case "lookup": return "lookup";
        case "string":
        case "long-string": return "text";
        default: return "text";
    }
}

function tableSchema<T extends TID>(name: string, rows: T[], columns: Column<T>[]) {
    const sampleRow = rows[0];

    return {
        table: name,
        rowCount: rows.length,
        columns: columns.map(col => {
            const editable = sampleRow ? getEditable(col, sampleRow) : col.editable;

            const info: {
                name: string;
                type: string;
                filterable: boolean;
                sortable: boolean;
                editable: boolean;
                alignRight: boolean;
                nullable: boolean;
                options?: string[];
            } = {
                name: col.name,
                type: columnDataType(col, sampleRow),
                filterable: !!col.filter,
                sortable: !!col.sort,
                editable: isEditable(col, [{key: "", rows: rows}]),
                alignRight: !!col.alignRight,
                nullable: !!col.nullable,
            };

            // surface the allowed values for select columns so the agent can edit them
            if(editable?.type === "select" && sampleRow) {
                info.options = editable.options(dummySelectWrapper(sampleRow))
                    .filter(o => !!o)
                    .map(o => o.display);
            }

            return info;
        }),
    };
}

const maxSearchResults = 50;

// matchColumn applies a single column's own filter (or a plain text match) for a query term.
// col.filter / matchAnywhere handle "|" OR splitting internally, so callers get OR-within-term for free.
function matchColumn<T extends TID>(row: T, col: Column<T>, term: string): boolean {
    if(col.filter) return col.filter(row, term);
    return matchAnywhere(cellText(row, col), term);
}

function searchTable<T extends TID>(rows: T[], columns: Column<T>[], query: string, columnFilters: {[column: string]: string}, outputColumns?: string[]) {
    const q = query.trim().toLowerCase();

    // resolve named column filters to (column, term) pairs, tracking any names that don't exist
    const requested = Object.entries(columnFilters)
        .map(([name, value]) => ({name, term: (value || "").trim().toLowerCase(), column: columns.find(c => c.name === name)}))
        .filter(f => f.term !== "");

    const unknownColumns = requested.filter(f => !f.column).map(f => f.name);
    const activeFilters = requested.filter(f => f.column) as {name: string; term: string; column: Column<T>}[];

    // resolve requested output columns to actual columns; unknown names are reported but ignored
    const unknownOutputColumns = (outputColumns || []).filter(name => !columns.some(c => c.name === name));
    const selectedColumns = outputColumns && outputColumns.length > 0
        ? columns.filter(c => outputColumns.includes(c.name))
        : columns;

    if(q === "" && activeFilters.length === 0) {
        return {
            error: `provide a non-empty "query" and/or "columnFilters" to search`,
            ...(unknownColumns.length ? {unknownColumns} : {}),
        };
    }

    const matches = rows.filter(row => {
        // "query" matches across all columns (OR over columns)
        if(q !== "" && !columns.some(col => matchColumn(row, col, q))) return false;
        // per-column filters must all match (AND across the named columns)
        return activeFilters.every(f => matchColumn(row, f.column, f.term));
    });

    const results = matches.slice(0, maxSearchResults).map(row => {
        const cells: {[name: string]: string} = {};
        selectedColumns.forEach(col => {
            cells[col.name] = cellText(row, col);
        });

        return {id: row.id, cells};
    });

    return {
        total: matches.length,
        truncated: matches.length > maxSearchResults,
        results: results,
        ...(unknownColumns.length ? {unknownColumns} : {}),
        ...(unknownOutputColumns.length ? {unknownOutputColumns} : {}),
    };
}

// applyEdit mirrors EditCell.applyUpdate in TableEditCell.tsx, producing an
// updated copy of the row that can be passed to EditContext.update()
function applyEdit<T extends TID>(row: T, column: Column<T>, strValue: string): T {
    const editable = getEditable(column, row);
    if(!editable) throw new Error(`column "${column.name}" is not editable`);

    const editKey = getEditKey(column, row);
    const rawValue = editKey ? lookupNestedProp(row, editKey) : null;

    let value: any = strValue;

    if(editable.parseValue) {
        value = editable.parseValue(rawValue, strValue);
    } else {
        switch(editable.type) {
            case "number":
                value = Calculator.eval(strValue);
                break;
            case "checkbox":
                value = parseBool(strValue);
                break;
            case "select": {
                const options = editable.options(dummySelectWrapper(row));
                const match = options.find(o => o && (String(o.value) === strValue || o.display?.toLowerCase() === strValue.toLowerCase()));
                if(!match) throw new Error(`"${strValue}" isn't a valid option for "${column.name}" (options: ${options.map(o => o?.display).filter(Boolean).join(", ")})`);
                value = match.value;
                break;
            }
            case "date":
            case "lookup":
            case "custom":
            case "button":
                throw new Error(`editing "${column.name}" (${editable.type}) isn't supported via webmcp yet`);
        }
    }

    if(editable.validate) {
        editable.validate(value);
    }

    if(editable.type === "string" && editable.autoFormat) {
        value = autoFormat(value, editable.autoFormat);
    }

    const updated = Object.assign({}, row, {
        // special format for backend, backend will overwrite this value
        updatedAt: "2000-01-01T01:11:22.264147Z",
    }) as T;

    if(editable.onChangeBeforeSave) {
        editable.onChangeBeforeSave(updated, strValue);
    }

    if(column.editObj) {
        column.editObj(updated, row, value);
    } else {
        if(!editKey) throw new Error(`column "${column.name}" is missing an editKey`);
        setNestedProp(updated, editKey, value);
    }

    return updated;
}

function parseBool(input: string): boolean | null {
    const v = input.trim().toLowerCase();
    if(v === "" || v === "null" || v === "indeterminate") return null;
    return v === "true" || v === "yes" || v === "1" || v === "y";
}

function toolResult(payload: any) {
    return {
        content: [{type: "text", text: JSON.stringify(payload)}],
    };
}
